import i18n from "i18next";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  GENERIC_FILTER,
  SENSOR_ID_FILTER,
  SENSOR_READINGS_GENERIC_FILTER,
  VEHICLES_GENERIC_FILTER,
} from "shared/filterDefinitions";

import { SchemaEntry } from "features/ui/MaterialTable";

import { SchemaOverride, UseSchema } from "./types";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";

const useSensorReadingsSchema = (accessorsToHide: string[] = []): UseSchema => {
  const t = i18n.t;
  const { attributes } = useAttributesContext(
    ContextProviderName.SensorReading
  );

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    SENSOR_READINGS_GENERIC_FILTER,
    attributeMap
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);
  const overrides: Record<string, SchemaOverride> = {
    VIN: {
      filterOverride: VEHICLES_GENERIC_FILTER({
        fieldName: "VIN",
        label: getDisplayLabel("VIN", "VIN"),
        search: true,
        disableFiltering: true,
      }),
    },
    sensorID: {
      schemaOverride: {
        filter: SENSOR_ID_FILTER({
          fieldName: "sensorID",
          fieldNameForAPI: "ID",
          label: getDisplayLabel("sensorID", t("common:columns.sensor_id")),
          search: true,
          disableSelectFilters: false,
        }),
      },
    },
  };

  const schema: SchemaEntry<string>[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    undefined,
    accessorsToHide
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaForAccessor,
    getSchemaEntry,
  };
};

const useSensorReadingsSchemaV2 = (
  accessorsToHide: string[] = []
): UseSchema => {
  const t = i18n.t;
  const { attributes } = useAttributesContext(
    ContextProviderName.SensorReadingV2
  );

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    SENSOR_READINGS_GENERIC_FILTER,
    attributeMap
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);
  const overrides: Record<string, SchemaOverride> = {
    assetID: {
      filterOverride: GENERIC_FILTER({
        fieldName: "assetID",
        label: getDisplayLabel("assetID", "assetID"),
        search: true,
        disableFiltering: true,
      }),
    },
    sensorID: {
      schemaOverride: {
        filter: SENSOR_ID_FILTER({
          fieldName: "sensorID",
          fieldNameForAPI: "ID",
          label: getDisplayLabel("sensorID", t("common:columns.sensor_id")),
          search: true,
          disableSelectFilters: false,
        }),
      },
    },
  };

  const schema: SchemaEntry<string>[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    undefined,
    accessorsToHide
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaForAccessor,
    getSchemaEntry,
  };
};

export { useSensorReadingsSchemaV2 };

export default useSensorReadingsSchema;
